import { useStaticQuery, graphql } from 'gatsby';
import { useIntl } from 'react-intl';

interface ContentfulSystemPage {
  systemPages: {
    nodes: {
      path: string;
      slug: string;
      title: string;
      node_locale: string;
      pageSytemType: string;
    }[];
  };
}

interface SystemPage<T> {
  page: T;
  title: string;
  path: string;
  slug: string;
}

/**
 * Hook for fetching the pageSystemType title and path
 * @param pageSystemTypes - arguments list of pageSytemTypes
 * @returns
 */
function useSystemPages<T extends readonly string[]>(
  ...pageSystemTypes: T
): { [K in keyof T]: SystemPage<T[K]> };

function useSystemPages<T extends readonly string[]>(
  ...args: [...T, boolean]
): { [K in keyof T]: SystemPage<T[K]> };

function useSystemPages(...args: any[]) {
  const lastArg = args.at(-1);
  const hasThrowOnErrorArg = typeof lastArg === 'boolean';
  const throwOnError = hasThrowOnErrorArg ? lastArg as boolean : true;
  const pageSystemTypes = (hasThrowOnErrorArg ? args.slice(0, -1) : args) as string[];

  const { locale } = useIntl();
  const { systemPages } = useStaticQuery<ContentfulSystemPage>(graphql`
    query {
      systemPages: allContentfulPageSystem {
        nodes {
          path
          slug
          title
          node_locale
          pageSytemType
        }
      }
    }
  `);

  const pages = pageSystemTypes.map((pageSystemType) => {
    const systemPage = systemPages.nodes.find(
      (node) => node.node_locale === locale && node.pageSytemType === pageSystemType,
    );

    if (!systemPage) {
      // We need this console during Gatsby SSR, otherwise it'll just say the page can't be SSR'd
      // and you'd be clueless as to why
      console.error(`Cannot find system page '${pageSystemType}'`);
      if (throwOnError) {
        throw new Error(`Cannot find system page '${pageSystemType}'`);
      }

      return null;
    }

    return {
      page: systemPage.pageSytemType,
      path: systemPage.path,
      slug: systemPage.slug,
      title: systemPage.title,
    };
  });

  return pages as any;
}

export default useSystemPages;
